import React from "react";
import PropTypes from "prop-types";
import {
  HighlightProvider,
  ContentDropdown,
  Image,
  ZoomImageWrapper,
} from "@learningblocks/shared-components";
import { PlainText } from "@learningblocks/simulations/shared-components";
import { useIntl } from "react-intl";
import cn from "./style.module.scss";

const TYPE_OBJECT = "object";

/**
 * NotebookImage:
 *  - Component to display Images in Notebook [Experimental Designs]
 *  - Display Image with/without caption
 */
export function NotebookImage(props) {
  const intl = useIntl();
  const className = `${cn.image} ${props.className} notebook-image-component`;
  const { onPlainTextClick } = React.useContext(HighlightProvider.Context);

  const onClickHighlight = (contentId, event) => {
    onPlainTextClick(contentId, event);
  };

  if (typeof props.image === TYPE_OBJECT) {
    return (
      <figure className={className}>
        <Image
          src={props.image.src.replace("{{locale}}", intl.locale)}
          alt={
            props.image.altText &&
            intl.formatMessage({
              id: props.image.altText,
              defaultMessage: props.image.altText,
            })
          }
          width="100%"
        />
        <ZoomImageWrapper
          tabIndex={props.tabIndex}
          url={props.image.src.replace("{{locale}}", intl.locale)}
          className={cn.zoomWrapper}
        />
        <PlainText
          text={props.image.caption}
          onClickHighlight={onClickHighlight}
        />
        {props.image.contentDropdown !== undefined ? (
          <ContentDropdown contentDetails={props.image.contentDropdown} />
        ) : null}
      </figure>
    );
  } else {
    return (
      <>
        <Image
          width="100%"
          src={props.image}
          alt={intl.formatMessage({
            id: props.image.altText,
            defaultMessage: props.image.altText,
          })}
          className={className}
          tabIndex={props.tabIndex}
        />
        <ZoomImageWrapper url={props.image} className={cn.zoomWrapper} />
      </>
    );
  }
}

NotebookImage.propTypes = {
  /**
   * tabIndex to set on component
   */
  tabIndex: PropTypes.number,
  /**
   *  ClassName
   */
  className: PropTypes.string,
  /**
   * Image to display
   *  - could be simply a url to image
   *  - or could be an object of type {src,caption}
   */
  image: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      /**
       * Url to image
       */
      src: PropTypes.string,
      altText: PropTypes.string,
      /**
       * caption for image
       */
      caption: PropTypes.string,
    }),
  ]),
  /**
   * Callback for when Highlighted text is clicked,
   *  - Used in PlainText componnet which is used when image
   *    is displayed with caption.
   */
  onClickHighlight: PropTypes.func,
  preview: PropTypes.bool,
};

NotebookImage.defaultProps = {
  tabIndex: 0,
  className: "",
  preview: false,
};
